import { useStores } from '@littlebird/sdk';
import { useParams, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { chatFindTunnel } from '@/components/blocks/Message/MessageList/useChatFind';
import { StaffBackendLabel } from '@/components/blocks/staff-backend-label';
import { Badge, IconButton, XStack } from '@/components/ui';
import { EditableTitle } from '@/components/ui/EditableTitle';
import { Header } from '@/components/ui/Header';
import { pageHeaderTunnel } from '@/design-system-v2/page-header-tunnel';
import { chatDebugStore } from '@/features/chat-debug';
import { chatRowLayoutStore } from '@/features/chat/chat-row-layout.store';
import { useCurrentChatMode } from '@/features/chat/chat.hooks';
import { ShareChatPopover } from '@/features/chat/chat.modals';
import { electronApis } from '@/features/electron-api';
import { FlagsButton } from '@/features/feature-flags/flags-button';
import { homeSettingsTunnel } from '@/features/home';
import {
  getHummingbirdDraftContext,
  stripHummingbirdModeAlias,
} from '@/features/hummingbird/hummingbird-mode.utils';
import { ImChannelChatBadge } from '@/features/msg-channels/common/im-channel-chat-badge';
import { ProjectBackLink } from '@/features/projects/projects';
import { LocalTerminalToggle } from '@/features/sprites/LocalTerminalToggle';
import { useHashRoute } from '@/hooks/useHashRoute';
import { settingsStore } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { isMac } from '@/utils/capabilities';

import { ChatModeDropdown } from './ChatModeDropdown';
import { DebugDrawer } from './DebugDrawer';

const ChatHeader = observer(() => {
  const { threads, projects } = useStores();
  const { chatId } = useParams({ strict: false });
  const isListPage = useRouterState({
    select: (state) => state.location.pathname === '/chats/all',
  });

  const thread = threads.getById(chatId ?? '');
  const project = thread?.projectId
    ? (projects.getById(thread.projectId) ?? null)
    : null;
  const canAccessDebug = thread && userStore.isInternalUser;

  return (
    <>
      <Header
        left={
          thread && !uiStore.chromeless ? (
            <XStack className="items-center gap-1 px-1">
              {project && (
                <>
                  <ProjectBackLink project={project} />
                  <Icons.chevronRight className="size-3 text-foreground-muted" />
                </>
              )}
              <EditableTitle
                key={`chat-title-${thread.id}`}
                value={thread.title || 'Untitled Chat'}
                onEdit={(title) => threads.updateThread(thread, { title })}
              />
              <ImChannelChatBadge visible={thread.isMessagingChannel} />
              <StaffBackendLabel
                backend={thread.backend}
                className="text-xs text-foreground-muted"
              />
            </XStack>
          ) : undefined
        }
        right={
          <XStack className="items-center gap-2">
            {/* Fixed-width slot for find bar */}
            {!isListPage && (
              <div className="w-40">
                <chatFindTunnel.Out />
              </div>
            )}
            <pageHeaderTunnel.Out />
            {userStore.isInternalUser && !isListPage && (
              <XStack className="items-center gap-1">
                <ChatModeDropdown />
                {!thread && <FlagsButton />}
              </XStack>
            )}
            <XStack className="items-center gap-1">
              <homeSettingsTunnel.Out />
              {chatRowLayoutStore.isBrowserOpen &&
                chatRowLayoutStore.isBrowserDismissed && (
                  <IconButton
                    size="sm"
                    icon={Icons.www}
                    tooltip="Show live browser"
                    tooltipOptions={{ side: 'bottom' }}
                    aria-label="Show live browser"
                    onClick={() => chatRowLayoutStore.restoreBrowser()}
                  />
                )}
              {thread && <HiddenFromUserIcon hidden={thread.hiddenFromUser} />}
              {canAccessDebug && thread.origin === 'experiment' && (
                <Badge variant="secondary">Experiment</Badge>
              )}
              {canAccessDebug && (
                <IconButton
                  size="sm"
                  icon={Icons.debug}
                  onClick={() => {
                    const isDebugOpenForChat =
                      chatDebugStore.activeChatId === thread.id &&
                      chatDebugStore.active?.isOpen;

                    if (isDebugOpenForChat) {
                      chatDebugStore.active?.setIsOpen(false);
                    } else {
                      chatDebugStore.setActiveChatId(thread.id);
                      const session = chatDebugStore.active!;
                      session.setIsOpen(true);
                      session.fetchTracesForChat();
                    }
                  }}
                  tooltip="Debug Chat"
                  tooltipOptions={{ side: 'bottom' }}
                  className={
                    chatDebugStore.activeChatId === thread.id &&
                    chatDebugStore.active?.isOpen
                      ? 'bg-surface-hover text-foreground'
                      : ''
                  }
                />
              )}
              {!isListPage && <LocalTerminalToggle />}
              <OpenInQuickEntryButton />
              {thread && (
                <IconButton
                  size="sm"
                  icon={thread?.starred ? Icons.starFilled : Icons.star}
                  onClick={() => {
                    threads.updateThread(thread, { starred: !thread.starred });
                    trackEvent('CHAT_STARRED', {
                      chatId: thread.id,
                      favorite: !thread.starred,
                    });
                  }}
                  tooltip={`${thread?.starred ? 'Unfavorite' : 'Favorite'} chat`}
                  tooltipOptions={{ side: 'bottom' }}
                />
              )}
              {thread && !userStore.hasSignedBaa && (
                <ShareChatPopover chatIds={[thread.id]}>
                  <IconButton
                    size="sm"
                    id="share-chat-button"
                    icon={Icons.share}
                    tooltip="Share chat"
                    tooltipOptions={{ side: 'bottom' }}
                  />
                </ShareChatPopover>
              )}
            </XStack>
          </XStack>
        }
      />

      {canAccessDebug && <DebugDrawer chatId={thread.id} />}
    </>
  );
});

const OpenInQuickEntryButton = observer(() => {
  const { drafts, threads } = useStores();
  const { chatId } = useParams({ strict: false });
  const { setHash } = useHashRoute();
  const { currentChatMode } = useCurrentChatMode();
  const thread = threads.getById(chatId ?? '');

  const isHummingbirdEnabled = settingsStore.hummingbirdEnabled;

  if (!thread || !isMac()) {
    return null;
  }

  return (
    <>
      <IconButton
        size="sm"
        icon={Icons.pictureInPicture}
        onClick={(e) => {
          e.currentTarget.blur();
          if (isHummingbirdEnabled) {
            const draft = drafts.getDraft(thread.id);
            electronApis.hummingbird.openThreadInHummingbird({
              threadId: thread.id,
              draftContent: draft?.content ?? '',
              chatMode: stripHummingbirdModeAlias(currentChatMode),
              draftContext: getHummingbirdDraftContext(draft?.selectedTexts),
            });
            trackEvent('HUMMINGBIRD_OPENED_FROM_MAIN_WINDOW');
            return;
          }
          setHash('settings/Hummingbird');
        }}
        tooltip="Open in Hover"
        tooltipOptions={{ side: 'bottom' }}
      />
    </>
  );
});

export { ChatHeader };
